import { Anchor, Box, List, Text, type TextProps, Title } from "@mantine/core";
import type { Components } from "react-markdown";
import React, { useMemo } from "react";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { accentDividerStyle, accentRuleStyle } from "../theme/darkSurfaceUi";

interface IframeComponentProps {
  src?: string;
  node?: unknown;
  allowFullScreen?: boolean;
  frameBorder?: string | number;
  style?: React.CSSProperties;
}

const EmElmt = (props: React.HTMLAttributes<HTMLElement>) => <em style={{ fontStyle: "italic" }} {...props} />;

const StrongElmt = (props: React.HTMLAttributes<HTMLElement>) => <strong style={{ fontWeight: "bold" }} {...props} />;

const markdownComponents: Components = {
  h1: (props) => <Title {...props} order={2} style={{ marginBottom: "0.5rem", marginTop: "1rem" }} />,
  h2: (props) => <Title {...props} order={3} style={{ marginBottom: "0.5rem", marginTop: "1rem" }} />,
  h3: (props) => <Title {...props} order={4} style={{ marginBottom: "0.5rem", marginTop: "1rem" }} />,
  h4: (props) => <Title {...props} order={5} style={{ marginTop: "1.5rem" }} />,
  ul: (props) => (
    <List type="unordered" withPadding style={{ paddingLeft: "1rem", marginBottom: "0.5rem" }} {...props} />
  ),
  ol: (props) => {
    const { ...restProps } = props as React.HTMLAttributes<HTMLUListElement>;
    return <List type="ordered" withPadding style={{ paddingLeft: "1rem", marginBottom: "0.5rem" }} {...restProps} />;
  },
  em: (props) => <EmElmt {...props} />,
  strong: (props) => <StrongElmt {...props} />,
  li: (props) => <List.Item style={{ paddingBottom: "0.25rem" }} {...props} />,
  br: () => <div />,
  iframe: ({
    src: initialSrc,
    allowFullScreen: initialAllowFullScreen = true,
    frameBorder: initialFrameBorder = "0",
    ...restProps
  }: IframeComponentProps) => {
    let currentSrc = initialSrc;
    if (currentSrc?.includes("youtube.com/embed")) {
      currentSrc = currentSrc.replace("youtube.com", "youtube-nocookie.com");
      try {
        const url = new URL(currentSrc);
        url.searchParams.set("controls", "0");
        url.searchParams.set("showinfo", "0");
        url.searchParams.set("modestbranding", "1");
        currentSrc = url.toString();
      } catch (error) {
        console.error("Error parsing iframe src URL:", error);
      }
    }

    return (
      <iframe
        src={currentSrc}
        allowFullScreen={initialAllowFullScreen}
        frameBorder={initialFrameBorder}
        style={{
          aspectRatio: "16/9",
          borderRadius: "12px",
          margin: "1rem 0",
          width: "100%",
          ...restProps.style,
        }}
      />
    );
  },
};

function mergeMarkdownComponents(
  paragraphTextProps?: TextProps,
  accentHorizontalRules?: boolean,
  accentDocumentTitle?: boolean,
  accentSectionDividers?: boolean,
  accentEmailLinks?: boolean,
): Components {
  const documentTitle: NonNullable<Components["h1"]> = (props) => (
    <Box mb="xl">
      <Title {...props} order={2} fw="bold" mt="md" mb="sm" />
      <Box style={accentDividerStyle} />
    </Box>
  );
  const sectionTitle: NonNullable<Components["h2"]> = (props) => (
    <>
      <Box component="hr" style={accentRuleStyle} my="xl" />
      <Title {...props} order={3} fw="bold" mb="sm" />
    </>
  );

  return {
    ...markdownComponents,
    ...(accentDocumentTitle
      ? {
          h1: documentTitle,
          h3: documentTitle,
        }
      : {}),
    ...(accentSectionDividers
      ? {
          h2: sectionTitle,
          h4: sectionTitle,
        }
      : {}),
    ...(accentEmailLinks
      ? {
          a: ({ href, children }) => (
            <Anchor href={href} c={href?.startsWith("mailto:") ? "var(--accent-on-dark)" : undefined}>
              {children}
            </Anchor>
          ),
        }
      : {}),
    ...(accentHorizontalRules
      ? {
          hr: () => <Box component="hr" style={accentRuleStyle} my="xl" />,
        }
      : {}),
    p: (props) => (
      <Text {...paragraphTextProps} style={{ marginBottom: "0.5rem", ...paragraphTextProps?.style }} {...props} />
    ),
  };
}

export type StyledMarkdownProps = {
  content: string;
  paragraphTextProps?: TextProps;
  accentHorizontalRules?: boolean;
  accentDocumentTitle?: boolean;
  accentSectionDividers?: boolean;
  accentEmailLinks?: boolean;
};

export function StyledMarkdown({
  content,
  paragraphTextProps,
  accentHorizontalRules,
  accentDocumentTitle,
  accentSectionDividers,
  accentEmailLinks,
}: StyledMarkdownProps) {
  const components = useMemo(
    () =>
      mergeMarkdownComponents(
        paragraphTextProps,
        accentHorizontalRules,
        accentDocumentTitle,
        accentSectionDividers,
        accentEmailLinks,
      ),
    [paragraphTextProps, accentHorizontalRules, accentDocumentTitle, accentSectionDividers, accentEmailLinks],
  );

  return (
    <Box className="markdown">
      <Markdown components={components} remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
        {content}
      </Markdown>
    </Box>
  );
}
